import { useCallback, useEffect, useRef, useState } from "react";
import { useLocalParticipant } from "@livekit/components-react";
import { LocalAudioTrack, Track } from "livekit-client";

import {
    BACKGROUND_TRACK_GAIN,
    backgroundTrackSrc,
} from "../domain/backgroundTracks";
import { BackgroundTrackId } from "../domain/types";
import { MEDITATION_TRACK_NAME } from "../domain/meditationTrack";

export type StageError = "playback" | "publish";

export type MeditationStage = {
    /** True once something is loaded and ready to play. */
    ready: boolean;
    playing: boolean;
    /** Seconds into the recording, or into the reading of a script. */
    position: number;
    /** The recording's length; 0 for a script, which has no fixed one. */
    duration: number;
    error: StageError | null;
    load: (options: {
        voiceUrl: string | null;
        backgroundTrack: BackgroundTrackId;
        /** A bed of the therapist's own, which wins over the catalogue one. */
        backgroundUrl?: string | null;
        /** The mix, as approved in the studio. */
        voiceVolume?: number;
        backgroundVolume?: number;
    }) => Promise<void>;
    play: () => Promise<void>;
    pause: () => void;
    seek: (seconds: number) => void;
    /** Take everything down: stop the sound, unpublish the track. */
    close: () => Promise<void>;
};

/** Ticks the position often enough that a progress bar looks live. */
const POSITION_TICK_MS = 250;

/** The hardware buttons a headset or keyboard sends through the Media Session API. */
const HEADSET_ACTIONS: readonly MediaSessionAction[] = ["pause", "stop"];

/**
 * Point the headset's pause and stop buttons at `handler`, or give them back.
 *
 * Some browsers do not know every action and throw for it; a button we could
 * not take over is only a button that still works.
 */
function setHeadsetHandler(handler: MediaSessionActionHandler | null) {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    HEADSET_ACTIONS.forEach((action) => {
        try {
            navigator.mediaSession.setActionHandler(action, handler);
        } catch {
            // Unsupported action.
        }
    });
}

/**
 * The sound of a meditation, in the therapist's browser and in the call.
 *
 * A meditation is heard by two people who must hear the same thing at the same
 * moment. Sending the patient a link to stream for themselves would drift — a
 * bar or two apart is enough to break a guided breath — so the therapist's
 * browser plays it once and publishes that audio into the room as a track,
 * the same way sharing a tab's sound does. The patient's side needs nothing
 * new for it: LiveKit's `RoomAudioRenderer` already plays what arrives.
 *
 * Both the recording and the background bed go through one Web Audio graph and
 * out of one destination, so they reach the patient mixed exactly as the
 * therapist hears them. The graph is also wired to the local speakers, because
 * `createMediaElementSource` takes an element off the default output — without
 * that second connection the therapist would be guiding a meditation they
 * cannot hear.
 *
 * A script with a bed and no recording is the same machinery with the voice
 * left out: the therapist's own microphone is already in the call, and the bed
 * plays under it.
 */
export function useMeditationStage(): MeditationStage {
    const { localParticipant } = useLocalParticipant();

    const [ready, setReady] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [position, setPosition] = useState(0);
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState<StageError | null>(null);

    const contextRef = useRef<AudioContext | null>(null);
    const voiceRef = useRef<HTMLAudioElement | null>(null);
    const bedRef = useRef<HTMLAudioElement | null>(null);
    const publishedRef = useRef<LocalAudioTrack | null>(null);
    const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Set while a script is being read aloud, which has no media element to
    // ask for a position.
    const scriptStartedAtRef = useRef<number | null>(null);
    const scriptOffsetRef = useRef(0);
    /*
     * Whether the therapist means it to be playing.
     *
     * Bluetooth headsets pause media on their own — taking one ear out, the
     * microphone the call opens switching the headset's profile, a stray tap.
     * In a session that silences a guided meditation for the patient with no
     * explanation on either side, so only the app's own controls stop it: a
     * pause nobody asked for here is undone.
     */
    const intendedPlayingRef = useRef(false);

    const stopTicking = useCallback(() => {
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    }, []);

    const teardown = useCallback(async () => {
        intendedPlayingRef.current = false;
        setHeadsetHandler(null);
        stopTicking();

        const track = publishedRef.current;
        publishedRef.current = null;
        if (track) {
            try {
                await localParticipant.unpublishTrack(track, true);
            } catch {
                // The room may already have taken it down — on a disconnect,
                // or when the call ended under us.
                track.stop();
            }
        }

        [voiceRef, bedRef].forEach((ref) => {
            if (ref.current) {
                ref.current.pause();
                ref.current.src = "";
                ref.current = null;
            }
        });

        const context = contextRef.current;
        contextRef.current = null;
        void context?.close().catch(() => undefined);

        scriptStartedAtRef.current = null;
        scriptOffsetRef.current = 0;
    }, [localParticipant, stopTicking]);

    const load = useCallback(
        async ({
            voiceUrl,
            backgroundTrack,
            backgroundUrl = null,
            voiceVolume = 1,
            backgroundVolume = BACKGROUND_TRACK_GAIN,
        }: {
            voiceUrl: string | null;
            backgroundTrack: BackgroundTrackId;
            backgroundUrl?: string | null;
            voiceVolume?: number;
            backgroundVolume?: number;
        }) => {
            await teardown();
            setError(null);
            setReady(false);
            setPlaying(false);
            setPosition(0);
            setDuration(0);

            try {
                const AudioContextCtor =
                    window.AudioContext ??
                    (
                        window as unknown as {
                            webkitAudioContext?: typeof AudioContext;
                        }
                    ).webkitAudioContext;
                const context = new AudioContextCtor();
                contextRef.current = context;

                const roomOutput = context.createMediaStreamDestination();

                const resumeUnaskedPause = (element: HTMLAudioElement) => {
                    element.addEventListener("pause", () => {
                        if (intendedPlayingRef.current && !element.ended) {
                            void element.play().catch(() => undefined);
                        }
                    });
                };

                const attach = (element: HTMLAudioElement, gainValue: number) => {
                    const gain = context.createGain();
                    gain.gain.value = gainValue;
                    context.createMediaElementSource(element).connect(gain);
                    gain.connect(roomOutput);
                    // …and to the therapist's own speakers, which the element
                    // no longer reaches on its own.
                    gain.connect(context.destination);
                };

                if (voiceUrl) {
                    const voice = new Audio(voiceUrl);
                    voice.crossOrigin = "anonymous";
                    voice.preload = "auto";
                    voice.onended = () => {
                        intendedPlayingRef.current = false;
                        setHeadsetHandler(null);
                        setPlaying(false);
                        stopTicking();
                    };
                    voiceRef.current = voice;
                    resumeUnaskedPause(voice);
                    attach(voice, voiceVolume);

                    await new Promise<void>((resolve) => {
                        if (voice.readyState >= 1) return resolve();
                        voice.onloadedmetadata = () => resolve();
                        voice.onerror = () => resolve();
                    });
                    // A blob-backed WebM can report `Infinity` until it is
                    // seeked; the caller's stored length covers that case, so
                    // an unusable value is reported as unknown rather than
                    // rendered as a broken progress bar.
                    setDuration(
                        Number.isFinite(voice.duration) ? voice.duration : 0
                    );
                }

                const bedSrc = backgroundUrl ?? backgroundTrackSrc(backgroundTrack);
                if (bedSrc) {
                    const bed = new Audio(bedSrc);
                    bed.crossOrigin = "anonymous";
                    bed.loop = true;
                    bedRef.current = bed;
                    resumeUnaskedPause(bed);
                    attach(bed, backgroundVolume);
                }

                const [roomTrack] = roomOutput.stream.getAudioTracks();
                if (roomTrack) {
                    // Published as screen-share audio: the same source a shared
                    // tab's sound uses, so nothing on the receiving side has to
                    // learn about meditations to play it. Named, so a session
                    // recorded track by track can leave it out of the record.
                    const track = new LocalAudioTrack(roomTrack, undefined, false);
                    await localParticipant.publishTrack(track, {
                        source: Track.Source.ScreenShareAudio,
                        name: MEDITATION_TRACK_NAME,
                    });
                    publishedRef.current = track;
                }

                setReady(true);
            } catch {
                await teardown();
                setError("publish");
            }
        },
        [localParticipant, stopTicking, teardown]
    );

    const startTicking = useCallback(() => {
        stopTicking();
        tickRef.current = setInterval(() => {
            const voice = voiceRef.current;
            if (voice) {
                setPosition(voice.currentTime);
                return;
            }
            const startedAt = scriptStartedAtRef.current;
            if (startedAt !== null) {
                setPosition(
                    scriptOffsetRef.current + (Date.now() - startedAt) / 1000
                );
            }
        }, POSITION_TICK_MS);
    }, [stopTicking]);

    const play = useCallback(async () => {
        // Browsers suspend a context created outside a gesture; this call is
        // always inside one, so resuming here is what makes the first play
        // actually sound.
        await contextRef.current?.resume().catch(() => undefined);
        try {
            await voiceRef.current?.play();
            await bedRef.current?.play();
        } catch {
            setError("playback");
            return;
        }
        if (!voiceRef.current) {
            scriptStartedAtRef.current = Date.now();
        }
        intendedPlayingRef.current = true;
        // Taken over only while it plays: the rest of the time the buttons
        // belong to whatever else the therapist is listening to.
        setHeadsetHandler(() => undefined);
        setPlaying(true);
        startTicking();
    }, [startTicking]);

    const pause = useCallback(() => {
        intendedPlayingRef.current = false;
        setHeadsetHandler(null);
        voiceRef.current?.pause();
        bedRef.current?.pause();
        if (!voiceRef.current && scriptStartedAtRef.current !== null) {
            scriptOffsetRef.current +=
                (Date.now() - scriptStartedAtRef.current) / 1000;
            scriptStartedAtRef.current = null;
        }
        setPlaying(false);
        stopTicking();
    }, [stopTicking]);

    const seek = useCallback((seconds: number) => {
        const target = Math.max(0, seconds);
        const voice = voiceRef.current;
        if (voice) {
            voice.currentTime = target;
        } else {
            scriptOffsetRef.current = target;
            if (scriptStartedAtRef.current !== null) {
                scriptStartedAtRef.current = Date.now();
            }
        }
        setPosition(target);
    }, []);

    const close = useCallback(async () => {
        await teardown();
        setReady(false);
        setPlaying(false);
        setPosition(0);
        setDuration(0);
    }, [teardown]);

    // Leaving the call mid-meditation must not leave a track published into a
    // room nobody is in.
    useEffect(
        () => () => {
            void teardown();
        },
        [teardown]
    );

    return {
        ready,
        playing,
        position,
        duration,
        error,
        load,
        play,
        pause,
        seek,
        close,
    };
}
